import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "api/axios";
import {
  buildTrackerRegistrationInitialValues,
  getTrackerProfile,
  getTrackerSelectOptions,
} from "config/trackerProfiles";
import Icon from "components/AppIcon";
import CoordinateMapPicker from "components/CoordinateMapPicker";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import {
  buildRegistrationSectionsFromProgram,
  getRegistrationFieldOptions,
  isRegistrationFieldRequired,
  isRegistrationFieldVisible,
  PROGRAM_METADATA_FIELDS,
  PROGRAM_RULE_FIELDS,
  PROGRAM_RULE_VARIABLE_FIELDS,
  TRACKER_GEOMETRY_FIELD_NAME,
} from "../programMetadata";
import { submitTrackerRegistration, uploadTrackerFileResource } from "../trackerRegistrationApi";
import {
  buildTrackerRegistrationPayload,
  getPhoneNumberValidationMessage,
} from "../trackerRegistrationPayload";
import { addGeneratedTrackerAttributeValues } from "utils/trackerBulkload";

function parseCoordinateValue(value) {
  if (!value) {
    return { latitude: "", longitude: "" };
  }

  if (typeof value === "object") {
    return {
      latitude: String(value?.latitude || ""),
      longitude: String(value?.longitude || ""),
    };
  }

  const stringValue = String(value).trim();

  if (!stringValue) {
    return { latitude: "", longitude: "" };
  }

  const bracketMatch = stringValue.match(/^\[\s*([^,\]]+)\s*,\s*([^,\]]+)\s*\]$/);
  if (bracketMatch) {
    return {
      latitude: String(bracketMatch[2] || "").trim(),
      longitude: String(bracketMatch[1] || "").trim(),
    };
  }

  const plainParts = stringValue.split(",").map((part) => part.trim()).filter(Boolean);
  if (plainParts.length === 2) {
    return {
      latitude: plainParts[1],
      longitude: plainParts[0],
    };
  }

  return { latitude: "", longitude: "" };
}

function normalizeFieldValueForForm(field, value) {
  if (field?.type === "coordinate") {
    return parseCoordinateValue(value);
  }

  return value ?? "";
}

function isFileValue(value) {
  return typeof File !== "undefined" && value instanceof File;
}

function getFileFieldLabel(value) {
  if (isFileValue(value)) {
    return value.name;
  }

  if (typeof value === "string" && value.trim()) {
    return "Existing uploaded file";
  }

  return "";
}

function flattenFields(sections) {
  return (sections || []).flatMap((section) => section?.fields || []);
}

async function uploadFileFields(formData, sections) {
  const fileFields = flattenFields(sections).filter((field) => field?.type === "file" && !field?.generated);

  if (fileFields.length === 0) {
    return formData;
  }

  const nextFormData = { ...formData };

  for (const field of fileFields) {
    const value = nextFormData[field.name];

    if (isFileValue(value)) {
      nextFormData[field.name] = await uploadTrackerFileResource(value);
    }
  }

  return nextFormData;
}

function getSelectedOption(options, value) {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  return options.find((option) => option.value === value) || { label: String(value), value };
}

function getNumberInputStep(field) {
  if (field?.type !== "number") return undefined;

  const valueType = String(field?.valueType || "").toUpperCase();
  return valueType === "NUMBER" || valueType === "PERCENTAGE" ? "any" : "1";
}

function TrackerFormField({ field, value, error, onChange, required = false, formData = {} }) {
  const wrapperClassName = field?.span === "full" ? "md:col-span-2" : "";

  if (field?.type === "select") {
    const options = getTrackerSelectOptions(getRegistrationFieldOptions(field, formData));

    return (
      <div className={wrapperClassName}>
        <Select
          label={field.label}
          required={required}
          options={options}
          value={getSelectedOption(options, value)}
          onChange={(option) => onChange(field.name, option?.value || "")}
          placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
          error={error}
          description={field?.description}
          disabled={field?.generated}
          searchable
        />
      </div>
    );
  }

  if (field?.type === "textarea") {
    return (
      <div className={wrapperClassName}>
        <label className="text-sm font-medium text-foreground block mb-2">
          {field.label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
        <textarea
          value={value || ""}
          onChange={(event) => onChange(field.name, event?.target?.value || "")}
          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
          rows={4}
          readOnly={field?.generated}
          disabled={field?.generated}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)]"
        />
        {field?.description && !error && (
          <p className="text-sm text-muted-foreground mt-1">{field.description}</p>
        )}
        {error && <p className="text-sm text-destructive mt-1">{error}</p>}
      </div>
    );
  }

  if (field?.type === "coordinate") {
    const coordinateValue = parseCoordinateValue(value);

    return (
      <div className={wrapperClassName}>
        <label className="text-sm font-medium text-foreground block mb-2">
          {field.label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            type="number"
            label="Latitude"
            value={coordinateValue.latitude}
            onChange={(event) =>
              onChange(field.name, {
                ...coordinateValue,
                latitude: event?.target?.value || "",
              })
            }
            placeholder="e.g. 5.6037"
            step="any"
            min="-90"
            max="90"
            disabled={field?.generated}
            readOnly={field?.generated}
          />
          <Input
            type="number"
            label="Longitude"
            value={coordinateValue.longitude}
            onChange={(event) =>
              onChange(field.name, {
                ...coordinateValue,
                longitude: event?.target?.value || "",
              })
            }
            placeholder="e.g. -0.1870"
            step="any"
            min="-180"
            max="180"
            disabled={field?.generated}
            readOnly={field?.generated}
          />
        </div>
        <div className="mt-3">
          <CoordinateMapPicker
            value={coordinateValue}
            onChange={(nextCoordinate) => onChange(field.name, nextCoordinate)}
            disabled={field?.generated}
            label={field.label}
          />
        </div>
        {field?.description && !error && (
          <p className="text-sm text-muted-foreground mt-1">{field.description}</p>
        )}
        {error && <p className="text-sm text-destructive mt-1">{error}</p>}
      </div>
    );
  }

  if (field?.type === "file") {
    const selectedFileLabel = getFileFieldLabel(value);

    return (
      <div className={wrapperClassName}>
        <label className="text-sm font-medium text-foreground block mb-2">
          {field.label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
        <input
          type="file"
          accept={String(field?.valueType || "").toUpperCase() === "IMAGE" ? "image/*" : undefined}
          onChange={(event) => onChange(field.name, event?.target?.files?.[0] || "")}
          disabled={field?.generated}
          className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground hover:file:bg-muted/80 focus-visible:outline-none focus-visible:border-[#16325a] focus-visible:shadow-[0_0_0_3px_rgba(22,50,90,0.12)] disabled:cursor-not-allowed disabled:opacity-50"
        />
        {selectedFileLabel && (
          <p className="text-sm text-muted-foreground mt-1">{selectedFileLabel}</p>
        )}
        {field?.description && !error && (
          <p className="text-sm text-muted-foreground mt-1">{field.description}</p>
        )}
        {error && <p className="text-sm text-destructive mt-1">{error}</p>}
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <Input
        type={field?.type || "text"}
        label={field.label}
        required={required}
        value={value || ""}
        onChange={(event) => onChange(field.name, event?.target?.value || "")}
        placeholder={field.placeholder}
        min={field?.type === "number" ? "0" : undefined}
        step={getNumberInputStep(field)}
        error={error}
        description={field?.description}
        readOnly={field?.generated}
        disabled={field?.generated}
      />
    </div>
  );
}

function buildInitialValuesFromSections(sections) {
  return (sections || []).reduce((acc, section) => {
    (section?.fields || []).forEach((field) => {
      if (!(field?.name in acc)) {
        acc[field.name] = field?.type === "coordinate" ? { latitude: "", longitude: "" } : "";
      }
    });
    return acc;
  }, {});
}

function clearHiddenRegistrationValues(sections, formData) {
  const nextFormData = { ...formData };

  flattenFields(sections).forEach((field) => {
    if (!isRegistrationFieldVisible(field, nextFormData)) {
      nextFormData[field.name] = "";
    }
  });

  return nextFormData;
}

function validateProgramSections(sections, formData) {
  const validationErrors = {};

  (sections || []).forEach((section) => {
    (section?.fields || []).forEach((field) => {
      if (field?.generated || !isRegistrationFieldVisible(field, formData)) {
        return;
      }

      const rawValue = formData?.[field.name];
      if (field?.type === "coordinate") {
        const coordinateValue = parseCoordinateValue(rawValue);
        const latitude = coordinateValue.latitude.trim();
        const longitude = coordinateValue.longitude.trim();
        const hasAnyCoordinateValue = Boolean(latitude || longitude);

        if (isRegistrationFieldRequired(field, formData) && !latitude && !longitude) {
          validationErrors[field.name] = `${field.label} requires both latitude and longitude`;
          return;
        }

        if (hasAnyCoordinateValue && (!latitude || !longitude)) {
          validationErrors[field.name] = `${field.label} requires both latitude and longitude`;
          return;
        }

        if (!hasAnyCoordinateValue) {
          return;
        }

        const parsedLatitude = Number(latitude);
        const parsedLongitude = Number(longitude);

        if (!Number.isFinite(parsedLatitude) || parsedLatitude < -90 || parsedLatitude > 90) {
          validationErrors[field.name] = "Latitude must be between -90 and 90";
          return;
        }

        if (!Number.isFinite(parsedLongitude) || parsedLongitude < -180 || parsedLongitude > 180) {
          validationErrors[field.name] = "Longitude must be between -180 and 180";
          return;
        }

        return;
      }

      if (field?.type === "file") {
        const hasFileValue = isFileValue(rawValue) || (typeof rawValue === "string" && rawValue.trim() !== "");

        if (isRegistrationFieldRequired(field, formData) && !hasFileValue) {
          validationErrors[field.name] = `${field.label} is required`;
        }

        return;
      }

      if (!isRegistrationFieldRequired(field, formData)) {
        const phoneValidationMessage = getPhoneNumberValidationMessage(field, rawValue);
        if (phoneValidationMessage) {
          validationErrors[field.name] = phoneValidationMessage;
        }

        return;
      }

      const hasValue =
        rawValue !== undefined &&
        rawValue !== null &&
        (typeof rawValue === "boolean" || String(rawValue).trim() !== "");

      if (!hasValue) {
        validationErrors[field.name] = `${field.label} is required`;
        return;
      }

      const phoneValidationMessage = getPhoneNumberValidationMessage(field, rawValue);
      if (phoneValidationMessage) {
        validationErrors[field.name] = phoneValidationMessage;
      }
    });
  });

  return validationErrors;
}

function buildTrackerJobStatusMessage(report) {
  const status = String(report?.status || "").trim().toUpperCase();

  if (!status) {
    return "";
  }

  if (status === "COMPLETED" || status === "OK") {
    return "Registration completed successfully.";
  }

  return `Registration job status: ${status}`;
}

export default function TrackerRegistrationWorkspace({
  trackerId = "",
  trackerLabel = "",
  programMetadata = null,
  currentOrgUnitId = "",
  editingRecord = null,
  onCancel,
  onSuccess,
  cancelLabel = "Cancel",
  className = "",
}) {
  const trackerProfile = useMemo(
    () => getTrackerProfile(trackerId, trackerLabel),
    [trackerId, trackerLabel],
  );
  const [resolvedProgramMetadata, setResolvedProgramMetadata] = useState(programMetadata || null);
  const [isProgramMetadataLoading, setIsProgramMetadataLoading] = useState(false);
  const [programMetadataError, setProgramMetadataError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitStatusText, setSubmitStatusText] = useState("");

  useEffect(() => {
    if (programMetadata) {
      setResolvedProgramMetadata(programMetadata);
      setProgramMetadataError("");
      setIsProgramMetadataLoading(false);
      return;
    }

    setResolvedProgramMetadata(null);
  }, [programMetadata]);

  useEffect(() => {
    if (programMetadata || !trackerId) {
      return undefined;
    }

    let isMounted = true;

    const loadProgramMetadata = async () => {
      setIsProgramMetadataLoading(true);
      setProgramMetadataError("");

      try {
        const response = await axios.get(`/programs/${trackerId}`, {
          params: {
            fields: PROGRAM_METADATA_FIELDS,
          },
        });

        if (isMounted) {
          setResolvedProgramMetadata(response?.data || null);
        }
      } catch (error) {
        console.error("Failed to load tracker registration metadata:", error);
        if (isMounted) {
          setResolvedProgramMetadata(null);
          setProgramMetadataError(
            "Unable to load the exact backend form for this tracker. Showing the current fallback form instead.",
          );
        }
      } finally {
        if (isMounted) {
          setIsProgramMetadataLoading(false);
        }
      }
    };

    loadProgramMetadata();

    return () => {
      isMounted = false;
    };
  }, [programMetadata, trackerId]);

  const backendSections = useMemo(
    () => buildRegistrationSectionsFromProgram(resolvedProgramMetadata),
    [resolvedProgramMetadata],
  );
  const isBackendDrivenForm = backendSections.length > 0;
  const isEditMode = Boolean(editingRecord);
  const sections = useMemo(
    () => (isBackendDrivenForm ? backendSections : (trackerProfile?.registration?.sections || [])),
    [backendSections, isBackendDrivenForm, trackerProfile],
  );
  const registrationConfig = useMemo(() => {
    if (isBackendDrivenForm) {
      return {
        title: isEditMode
          ? `Edit ${resolvedProgramMetadata?.displayName || trackerLabel || "Tracker Record"}`
          : `Register ${resolvedProgramMetadata?.displayName || trackerLabel || "Tracker Record"}`,
        subtitle: isEditMode
          ? "Update the details below to edit this record."
          : "Complete the details below to register a new record.",
        submitLabel: isEditMode ? "Save Changes" : "Register Record",
        successMessage: isEditMode ? "Record updated successfully!" : "Record registered successfully!",
      };
    }

    return trackerProfile?.registration || {};
  }, [isBackendDrivenForm, isEditMode, resolvedProgramMetadata?.displayName, trackerLabel, trackerProfile]);
  const editPrefill = useMemo(() => {
    if (!isEditMode || !Array.isArray(editingRecord?.attributes)) return null;
    const prefill = editingRecord.attributes.reduce((acc, item) => {
      const id = item?.attribute || item?.trackedEntityAttribute;
      if (id) acc[id] = item?.value ?? "";
      return acc;
    }, {});

    const geometry = editingRecord?.geometry;
    const coordinates = Array.isArray(geometry?.coordinates) ? geometry.coordinates : [];
    if (String(geometry?.type || "").toLowerCase() === "point" && coordinates.length >= 2) {
      prefill[TRACKER_GEOMETRY_FIELD_NAME] = {
        longitude: coordinates[0],
        latitude: coordinates[1],
      };
    }

    return prefill;
  }, [isEditMode, editingRecord]);

  const initialFormData = useMemo(() => {
    const base = isBackendDrivenForm
      ? buildInitialValuesFromSections(sections)
      : buildTrackerRegistrationInitialValues(trackerProfile);
    const normalizedBase = (sections || []).reduce((acc, section) => {
      (section?.fields || []).forEach((field) => {
        acc[field.name] = normalizeFieldValueForForm(field, base?.[field.name]);
      });
      return acc;
    }, {});

    if (editPrefill) {
      const normalizedEditPrefill = (sections || []).reduce((acc, section) => {
        (section?.fields || []).forEach((field) => {
          if (field?.name in editPrefill) {
            acc[field.name] = normalizeFieldValueForForm(field, editPrefill[field.name]);
          }
        });
        return acc;
      }, {});

      return { ...normalizedBase, ...normalizedEditPrefill };
    }

    return normalizedBase;
  }, [isBackendDrivenForm, sections, trackerProfile, editPrefill]);
  // `programId` and `trackedEntityTypeId` come from the selected tracker metadata.
  // In the modal path this is passed in directly; otherwise we fetch it by `trackerId`.
  const programId = resolvedProgramMetadata?.id || trackerId;
  const trackedEntityTypeId = resolvedProgramMetadata?.trackedEntityType?.id || "";

  useEffect(() => {
    if (!programId) {
      return undefined;
    }

    let isMounted = true;

    const loadProgramRuleMetadata = async () => {
      try {
        const [rulesResponse, variablesResponse] = await Promise.all([
          axios.get("/programRules", {
            params: {
              filter: `program.id:eq:${programId}`,
              paging: false,
              fields: PROGRAM_RULE_FIELDS,
            },
          }),
          axios.get("/programRuleVariables", {
            params: {
              filter: `program.id:eq:${programId}`,
              paging: false,
              fields: PROGRAM_RULE_VARIABLE_FIELDS,
            },
          }),
        ]);

        if (!isMounted) return;

        setResolvedProgramMetadata((current) => {
          if (!current || current.id !== programId) return current;

          return {
            ...current,
            programRules: Array.isArray(rulesResponse?.data?.programRules)
              ? rulesResponse.data.programRules
              : [],
            programRuleVariables: Array.isArray(variablesResponse?.data?.programRuleVariables)
              ? variablesResponse.data.programRuleVariables
              : [],
          };
        });
      } catch (error) {
        console.error("Failed to load tracker program rules:", error);
      }
    };

    loadProgramRuleMetadata();

    return () => {
      isMounted = false;
    };
  }, [programId]);

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const submitIntentRef = useRef("continue");
  const conditionalFields = useMemo(
    () =>
      flattenFields(sections).filter(
        (field) =>
          field?.visibleWhen?.fieldName ||
          field?.hiddenWhen?.fieldName ||
          (Array.isArray(field?.optionRules) && field.optionRules.length > 0),
      ),
    [sections],
  );

  const sanitizeConditionalFormData = useCallback((currentFormData) => {
    let hasChanges = false;
    const nextFormData = { ...currentFormData };

    conditionalFields.forEach((field) => {
      if (!isRegistrationFieldVisible(field, nextFormData)) {
        if (nextFormData[field.name]) {
          nextFormData[field.name] = "";
          hasChanges = true;
        }
        return;
      }

      if (field?.type !== "select") return;

      const selectedValue = nextFormData[field.name];
      if (!selectedValue) return;

      const allowedOptions = getRegistrationFieldOptions(field, nextFormData);
      const selectedOptionIsAllowed = allowedOptions.some(
        (option) => String(option?.value) === String(selectedValue),
      );

      if (!selectedOptionIsAllowed) {
        nextFormData[field.name] = "";
        hasChanges = true;
      }
    });

    return hasChanges ? nextFormData : currentFormData;
  }, [conditionalFields]);

  useEffect(() => {
    if (isEditMode) {
      // In edit mode, merge new base fields in but preserve already-set values
      setFormData((prev) => ({ ...initialFormData, ...prev }));
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    setIsSubmitting(false);
    setSubmitSuccess(false);
    setSubmitError("");
    setSubmitStatusText("");
  }, [initialFormData, isEditMode]);

  useEffect(() => {
    setFormData((currentFormData) => sanitizeConditionalFormData(currentFormData));
  }, [sanitizeConditionalFormData]);

  // Phase 1 keeps submission limited to backend-driven forms because those field
  // names already match DHIS2 attribute ids and can be transformed safely.
  const submissionBlockingMessage = useMemo(() => {
    if (!trackerId) {
      return "Choose a tracker from the trackers page to open the correct form.";
    }

    if (isProgramMetadataLoading) {
      return "Preparing the backend registration form.";
    }

    if (!currentOrgUnitId) {
      return "Choose a district or sub-area before registering a tracker record.";
    }

    if (!isBackendDrivenForm) {
      return "Phase 1 registration currently submits only backend-driven tracker forms.";
    }

    if (!programId || !trackedEntityTypeId) {
      return "Tracker details are still loading. Refresh and try again.";
    }

    return "";
  }, [
    currentOrgUnitId,
    isBackendDrivenForm,
    isProgramMetadataLoading,
    programId,
    trackedEntityTypeId,
    trackerId,
  ]);

  const isSubmitDisabled =
    isSubmitting || Boolean(submissionBlockingMessage) || sections.length === 0;

  const handleFieldChange = (fieldName, value) => {
    const dependentFields = conditionalFields.filter(
      (field) =>
        field?.visibleWhen?.fieldName === fieldName ||
        field?.hiddenWhen?.fieldName === fieldName ||
        (field?.optionRules || []).some((rule) => rule?.condition?.fieldName === fieldName),
    );

    setFormData((prev) => {
      const nextFormData = {
        ...prev,
        [fieldName]: value,
      };

      dependentFields.forEach((field) => {
        if (!isRegistrationFieldVisible(field, nextFormData)) {
          nextFormData[field.name] = "";
          return;
        }

        if (field?.type === "select") {
          const allowedOptions = getRegistrationFieldOptions(field, nextFormData);
          const selectedValue = nextFormData[field.name];
          const selectedOptionIsAllowed = allowedOptions.some(
            (option) => String(option?.value) === String(selectedValue),
          );

          if (selectedValue && !selectedOptionIsAllowed) {
            nextFormData[field.name] = "";
          }
        }
      });

      return nextFormData;
    });

    setErrors((prev) => {
      const nextErrors = { ...prev, [fieldName]: "" };
      dependentFields.forEach((field) => {
        nextErrors[field.name] = "";
      });
      return nextErrors;
    });

    if (submitError) {
      setSubmitError("");
    }

    if (submitStatusText) {
      setSubmitStatusText("");
    }

    if (submitSuccess) {
      setSubmitSuccess(false);
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    const submitIntent = isEditMode ? "save" : submitIntentRef.current || "continue";
    setSubmitError("");
    setSubmitSuccess(false);
    setSubmitStatusText("");

    if (submissionBlockingMessage) {
      setSubmitError(submissionBlockingMessage);
      return;
    }

    const submissionFormData = clearHiddenRegistrationValues(sections, formData);
    const validationErrors = validateProgramSections(sections, submissionFormData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSubmitStatusText("Submitting registration...");

    try {
      const preparedFormData = await uploadFileFields(submissionFormData, sections);
      const basePayload = buildTrackerRegistrationPayload({
        formData: preparedFormData,
        orgUnitId: currentOrgUnitId,
        programId,
        trackedEntityTypeId,
        sections,
        trackedEntityId: isEditMode
          ? editingRecord?.trackedEntity || editingRecord?.trackedEntityInstance || null
          : null,
      });
      const payload = isEditMode
        ? basePayload
        : await addGeneratedTrackerAttributeValues(basePayload, resolvedProgramMetadata);
      const registeredRecordId =
        payload?.trackedEntities?.[0]?.trackedEntity ||
        editingRecord?.trackedEntity ||
        editingRecord?.trackedEntityInstance ||
        "";

      const result = await submitTrackerRegistration(payload, {
        importStrategy: isEditMode ? "UPDATE" : "CREATE_AND_UPDATE",
        onStatus: (report) => {
          setSubmitStatusText(buildTrackerJobStatusMessage(report) || "Submitting registration...");
        },
      });

      if (!isEditMode && submitIntent === "add-new") {
        setFormData(initialFormData);
      }
      setSubmitStatusText("");
      setSubmitSuccess(true);
      onSuccess?.(
        {
          ...result,
          recordId: registeredRecordId,
          payload,
        },
        {
          intent: submitIntent,
          recordId: registeredRecordId,
          isEditMode,
        },
      );
    } catch (error) {
      setSubmitError(error?.message || "Registration could not be completed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={[
        "rounded-xl border border-border bg-card shadow-sm p-6 md:p-8",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      >
        {typeof document !== "undefined" && (submitError || submitStatusText || submitSuccess) && createPortal(
          <div className="fixed right-4 top-4 z-[1600] w-[calc(100%-2rem)] max-w-md">
            <div
              className={[
                "flex items-start gap-3 rounded-xl border p-4 shadow-xl",
                submitError
                  ? "border-destructive/20 bg-card text-destructive"
                  : submitSuccess
                    ? "border-success/20 bg-card text-success"
                    : "border-border bg-card text-muted-foreground",
              ].join(" ")}
            >
              <Icon
                name={submitError ? "AlertCircle" : submitSuccess ? "CheckCircle" : "Loader2"}
                size={20}
                className={submitStatusText && !submitSuccess && !submitError ? "mt-0.5 animate-spin" : "mt-0.5"}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">
                  {submitError ? "Registration failed" : submitSuccess ? "Registration successful" : "Submitting registration"}
                </p>
                <p className="mt-1 text-sm">
                  {submitError ||
                    (submitSuccess
                      ? registrationConfig?.successMessage || "Record registered successfully!"
                      : submitStatusText)}
                </p>
              </div>
              {(submitError || submitSuccess) && (
                <button
                  type="button"
                  onClick={() => {
                    setSubmitError("");
                    setSubmitSuccess(false);
                  }}
                  className="rounded-md p-1 transition-colors hover:bg-muted"
                  aria-label="Dismiss registration message"
                >
                  <Icon name="X" size={16} />
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}

        <div className="mb-8 flex flex-col gap-4 pr-10 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Icon name="ClipboardList" size={24} className="text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground md:text-3xl">
              {registrationConfig?.title || "Tracker Registration"}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {registrationConfig?.subtitle || "Capture a new tracker record."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
                {resolvedProgramMetadata?.displayName ||
                  trackerProfile?.trackerLabel ||
                  trackerLabel ||
                  "Generic Tracker"}
              </span>
            </div>
          </div>
        </div>

        {!trackerId && (
          <div className="max-w-md rounded-lg border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-warning">
            Choose a tracker from the trackers page to open the correct form.
          </div>
        )}
      </div>

      {isProgramMetadataLoading && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          <Icon name="Loader2" size={18} className="animate-spin" />
          Preparing form...
        </div>
      )}

      {programMetadataError && (
        <div className="mb-6 rounded-lg border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-warning">
          Some fields are still being prepared. A simplified form is shown for now, but submit is
          only enabled when the backend-driven tracker form is available.
        </div>
      )}

      {submissionBlockingMessage && trackerId && !isProgramMetadataLoading && (
        <div className="mb-6 rounded-lg border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-warning">
          {submissionBlockingMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {sections.length === 0 && (
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-4 text-sm text-muted-foreground">
            This form is still being prepared for this tracker.
          </div>
        )}

        {sections.map((section) => (
          <section key={section.title} className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">{section.title}</h2>
              {section.description && (
                <p className="mt-1 text-sm text-muted-foreground">{section.description}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {(section?.fields || [])
                .filter((field) => isRegistrationFieldVisible(field, formData))
                .map((field) => (
                  <TrackerFormField
                    key={field.name}
                    field={field}
                    required={isRegistrationFieldRequired(field, formData)}
                    formData={formData}
                    value={formData?.[field.name]}
                    error={errors?.[field.name]}
                    onChange={handleFieldChange}
                  />
                ))}
            </div>
          </section>
        ))}

        <div
          className={[
            "border-t border-border pt-4",
            onCancel || !isEditMode ? "flex flex-col gap-3 sm:flex-row" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {isEditMode ? (
            <Button
              type="submit"
              onClick={() => {
                submitIntentRef.current = "save";
              }}
              className={onCancel ? "sm:flex-1" : "w-full"}
              disabled={isSubmitDisabled}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="Loader2" size={18} className="animate-spin" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="Save" size={18} />
                  {registrationConfig?.submitLabel || "Save Changes"}
                </span>
              )}
            </Button>
          ) : (
            <>
              <Button
                type="submit"
                onClick={() => {
                  submitIntentRef.current = "continue";
                }}
                className="sm:flex-1"
                disabled={isSubmitDisabled}
              >
                {isSubmitting && submitIntentRef.current === "continue" ? (
                  <span className="flex items-center justify-center gap-2">
                    <Icon name="Loader2" size={18} className="animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Icon name="ArrowRight" size={18} />
                    Save and Continue
                  </span>
                )}
              </Button>

              <Button
                type="submit"
                variant="outline"
                onClick={() => {
                  submitIntentRef.current = "add-new";
                }}
                className="sm:flex-1"
                disabled={isSubmitDisabled}
              >
                {isSubmitting && submitIntentRef.current === "add-new" ? (
                  <span className="flex items-center justify-center gap-2">
                    <Icon name="Loader2" size={18} className="animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Icon name="Plus" size={18} />
                    Save and Add New
                  </span>
                )}
              </Button>
            </>
          )}

          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="sm:flex-1"
            >
              {cancelLabel}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
